import { createRouter, createWebHistory } from 'vue-router';
import { useAdminAuthStore } from '../store/adminAuth';
import { safeReturnPath } from '../auth/browser-session.mjs';
import { managementScope } from '../auth/management-context.mjs';
import { createAdminScrollBehavior } from './route-scroll.mjs';


const routes = [
    { path: '/management/enter', component: () => import('../views/ManagementEntryView.vue') },
    { path: '/management-ended', component: () => import('../views/ManagementEntryView.vue') },
    { path: '/', redirect: '/users' },
    {
        path: '/login',
        name: 'LoginBridge',
        component: () => import('../views/LoginBridgeView.vue'),
        meta: { breadcrumb: '统一登录' }
    },
    {
        path: '/login-complete',
        name: 'LoginComplete',
        component: () => import('../views/LoginBridgeView.vue'),
        props: { completion: true },
        meta: { breadcrumb: '完成登录' }
    },
    {
        path: '/users',
        name: 'UserList',
        component: () => import('../views/UserListView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '用户列表' }
    },
    {
        path: '/subscriptions',
        name: 'Subscriptions',
        component: () => import('../views/SubscriptionView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '订阅管理' }
    },
    {
        path: '/services',
        name: 'Services',
        component: () => import('../views/ServiceListView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '服务管理' }
    },
    {
        path: '/services/:serviceId',
        name: 'ServiceDetail',
        component: () => import('../views/ServiceDetailView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '服务配置' }
    },
    {
        path: '/applications',
        name: 'Applications',
        component: () => import('../views/ApplicationListView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '应用接入' }
    },
    {
        path: '/applications/:clientId',
        name: 'ApplicationDetail',
        component: () => import('../views/ApplicationDetailView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '应用配置与接入指南' }
    },
    {
        path: '/login-origins',
        name: 'LoginOrigins',
        component: () => import('../views/LoginOriginListView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '登录接入管理' }
    },
    {
        path: '/login-result-pages',
        name: 'LoginResultPages',
        component: () => import('../views/LoginResultPagesView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '登录完成页管理' }
    },
    {
        path: '/content',
        name: 'ContentCatalog',
        component: () => import('../views/ContentCatalogView.vue'),
        meta: { requiresAuth: true, adminLayout: true, breadcrumb: '官网运维' }
    },
    {
        path: '/unauthorized',
        name: 'Unauthorized',
        component: () => import('../views/UnauthorizedView.vue')
    }
];

const scrollBehavior = createAdminScrollBehavior(() => ({ left: window.scrollX, top: window.scrollY }));
const router = createRouter({
    history: createWebHistory(`${managementScope.prefix}/`),
    routes,
    scrollBehavior,
});

router.beforeEach(async (to, from, next) => {
    if (to.path !== from.path) scrollBehavior.remember(from);
    const authStore = useAdminAuthStore();
    if (to.path === '/management/enter' || to.path === '/management-ended') return next();
    if (!managementScope.prefix) {
        window.location.replace('/api/management/start/?' + new URLSearchParams({target:'admin', return_path:safeReturnPath(to.fullPath)}));
        return;
    }
    if (to.path === '/login' || to.path === '/login-complete' || to.path === '/unauthorized') {
        return next({path:'/management-ended', query:{reason:to.path === '/unauthorized' ? 'forbidden' : 'expired'}});
    }
    if ('token' in to.query) {
        const cleanQuery = { ...to.query };
        delete cleanQuery.token;
        return next({ path: to.path, query: cleanQuery, replace: true });
    }

    if (to.path === '/login' && authStore.isAuthenticated) {
        return next(safeReturnPath(to.query.return_to || '/users'));
    }
    if (!to.meta.requiresAuth) return next();
    if (!authStore.profileVerified) {
        try {
            await authStore.fetchProfile();
        } catch (error) {
            if (error.permissionDenied) return next({path:'/management-ended', query:{reason:'forbidden'}});
            if (!authStore.isAuthenticated || !authStore.user) {
                return next({
                    path: '/management-ended',
                    query: { reason: [401, 403, 410].includes(error.response?.status) ? 'expired' : 'unavailable' }
                });
            }
        }
    }
    if (!authStore.isAuthenticated) {
        return next({ path: '/management-ended', query: { reason:'expired' } });
    }
    if (!['admin', 'superuser'].includes(authStore.user?.role)) {
        return next('/unauthorized');
    }
    return next();
});

export default router;
